import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { useUpsertLead, useDeleteLead } from '@/entities/lead';
import { ListSelector } from '@/components/features/ListSelector';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import  { type LeadData } from './types';

interface LeadCardProps {
  hasChanges: boolean
  isNew: boolean
  lead: LeadData | null
  setHasChanges: (hasChanges: boolean) => void
  setIsNew: (isNew: boolean) => void
  onSaveSuccess?: () => void
  onDeleteSuccess?: () => void
}

interface DataFieldProps {
  id: string
  key: string
  value: string
}

function defineDataFields(data?: Record<string, unknown>): DataFieldProps[] {
  if (!data) {
    return []
  }

  return Object.entries(data).map(([key, value]) => ({
    id: key.replace(' ', '-'),
    key,
    value: String(value ?? ''),
  }))
}

function getDataForSave(dataFields: DataFieldProps[]): Record<string, string> {
  const result: Record<string, string> = {}

  dataFields.forEach((field) => {
    if (field.key.trim()) {
      result[field.key] = field.value
    }
  })
  return result
}

export function LeadCard({
  hasChanges,
  isNew,
  lead,
  setHasChanges,
  setIsNew,
  onSaveSuccess,
  onDeleteSuccess,
}: LeadCardProps) {
  const upsertLead = useUpsertLead()
  const deleteLead = useDeleteLead()

  const [dataFields, setDataFields] = useState<DataFieldProps[]>([])
  const [leadStatus, setLeadStatus] = useState('NEW')
  const [leadPhone, setLeadPhone] = useState('')
  const [listId, setListId] = useState<number | undefined>()

  useEffect(() => {
    if (lead) {
      setListId(lead.list_id)
      setLeadStatus(lead.status || 'NEW')
      setLeadPhone(lead.phone_number || '')
      setDataFields(defineDataFields(lead.data))
      setHasChanges(false)
      return
    }

    if (isNew) {
      setListId(undefined)
      setLeadStatus('NEW')
      setLeadPhone('')
      setDataFields([])
      return
    }
  }, [lead, isNew, setHasChanges])

  const isResetAllowed = useMemo(() => {
    return hasChanges && !upsertLead.isPending
  }, [hasChanges, upsertLead.isPending])

  const isSaveAllowed = useMemo(() => {
    return hasChanges && !!leadStatus && !!listId && !!leadPhone && !upsertLead.isPending
  }, [hasChanges, leadStatus, listId, leadPhone, upsertLead.isPending])

  const handleReset = () => {
    setListId(lead?.list_id || undefined)
    setLeadStatus(lead?.status || 'NEW')
    setLeadPhone(lead?.phone_number || '')
    setDataFields(defineDataFields(lead?.data))

    setHasChanges(false)
  }

  const handleSave = async () => {
    if (!listId || !leadPhone) return

    const payload: {
      id?: string
      list_id: number
      phone_number: string
      status: string
      data?: Record<string, string>
    } = {
      list_id: listId,
      phone_number: leadPhone,
      status: leadStatus,
      data: getDataForSave(dataFields),
    }

    if (!isNew && lead?.id) {
      payload.id = lead.id
    }

    try {
      const result = await upsertLead.mutateAsync(payload)
      toast.success(result.message)
      setHasChanges(false)
      setIsNew(false)
      onSaveSuccess?.()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save lead')
      setHasChanges(false)
    }
  }

  const handleDelete = async () => {
    if (!lead?.id) return
    if (!window.confirm('Delete this lead permanently? This action cannot be undone.')) return
    try {
      await deleteLead.mutateAsync(lead.id)
      toast.success('Lead deleted')
      onDeleteSuccess?.()
    } catch {
      toast.error('Failed to delete lead')
    }
  }

  const handleDataFieldAdd = () =>
    setDataFields((prev) => [...prev, { id: Date.now().toString(), key: '', value: '' }])

  const handleDataFieldUpdate = (id: string, field: 'key' | 'value', value: string) => {
    setDataFields((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)))
    setHasChanges(true)
  }

  const handleDataFieldDelete = (id: string) => {
    setDataFields((prev) => prev.filter((f) => f.id !== id))
    setHasChanges(true)
  }

  return (
    <Card>
      <CardHeader className="border-b bg-gray-50 py-2">
        <CardTitle className="flex items-center justify-between text-body">
          <div className="flex items-center gap-3">
            <span>Lead Data</span>
            {lead?.campaign_name && (
              <span className="text-detail font-normal text-gray-500">Campaign: {lead.campaign_name}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isNew && lead?.id && (
              <Button
                onClick={handleDelete}
                disabled={deleteLead.isPending}
                type="button"
                variant="outline"
                className="text-destructive hover:text-white hover:bg-destructive"
              >
                <Trash2 className="w-3 h-3" />
                {deleteLead.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            )}
            <Button onClick={handleReset} disabled={!isResetAllowed} type="button" variant="outline">
              Reset Changes
            </Button>
            <Button onClick={handleSave} disabled={!isSaveAllowed} type="button">
              {upsertLead.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <div className="grid auto-cols-fr grid-flow-col gap-4 p-6 border-b">
        <label className="grid gap-2">
          <span className="text-body font-medium whitespace-nowrap">Disposition</span>
          <Input
            value={leadStatus}
            onChange={(e) => {
              setLeadStatus(e.target.value)
              setHasChanges(true)
            }}
            placeholder="Enter status"
            className="h-10"
            name="status"
          />
        </label>

        {isNew && (
          <label className="grid gap-2">
            <span className="text-body font-medium whitespace-nowrap">Phone number</span>

            <Input
              value={leadPhone || ''}
              onChange={(e) => {
                setLeadPhone(e.target.value)
                setHasChanges(true)
              }}
              placeholder="Enter phone number"
              className="h-10"
              name="phone_number"
              required={true}
            />
          </label>
        )}

        <ListSelector
          labelClassName="text-body font-medium whitespace-nowrap"
          label={isNew ? 'Select a list' : 'List'}
          selectedListId={listId}
          onListChange={(value) => {
            if (value !== listId) {
              setListId(value)
              setHasChanges(true)
            }
          }}
        />
      </div>
      <CardContent className="pt-0 px-0 pb-2">
        <Table>
          <TableHeader>
            <TableRow className="h-7">
              <TableHead className="w-[160px] py-1">Field</TableHead>
              <TableHead className="py-1">Value</TableHead>
              <TableHead className="w-8 py-1" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataFields.map((field) => (
              <TableRow key={field.id} className="h-7">
                <TableCell className="py-0.5 px-2">
                  <Input
                    value={field.key}
                    onChange={(e) => handleDataFieldUpdate(field.id, 'key', e.target.value)}
                    placeholder="Field name"
                    className="h-6 border-0 shadow-none bg-transparent px-1 focus-visible:ring-1"
                    name={field.key}
                  />
                </TableCell>
                <TableCell className="py-0.5 px-2">
                  <Input
                    value={field.value}
                    onChange={(e) => handleDataFieldUpdate(field.id, 'value', e.target.value)}
                    placeholder="Value"
                    className="h-6 border-0 shadow-none bg-transparent px-1 focus-visible:ring-1"
                    name={field.value}
                  />
                </TableCell>
                <TableCell className="py-0.5 px-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDataFieldDelete(field.id)}
                    className="h-5 w-5 p-0 text-gray-400 hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-1 px-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDataFieldAdd}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Field
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}